import PropTypes from "prop-types";
import React, { useContext } from "react";

import { AuthContext, LoginForm, useUserInfo } from "@swa-ui/authentication";
import { useDeviceInfo } from "@swa-ui/browser";
import { Caption, HorizontalList, useCaption } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import styles from "./AnonymousInformation.module.scss";

/**
 * AnonymousInformation provides the anonymous corporate info for the header.
 */

export const AnonymousInformation = (props) => {
  const { light } = props;
  const { getCorporateInfo } = useUserInfo();
  const { handleLogout } = useContext(AuthContext);
  const { isLargeOrXlarge } = useDeviceInfo();
  const { name: companyName } = getCorporateInfo() ?? {};
  const { captionRef, captionLocation, hideCaption, isCaptionVisible, showCaption } =
    useCaption("below");

  return (
    <>
      <HorizontalList
        items={[...(isLargeOrXlarge ? getCustomerInfoItems() : []), ...getActionItems()]}
        {...getListProps()}
      />
    </>
  );

  function getListProps() {
    return {
      light,
      spacing: "small",
    };
  }

  function getCustomerInfoItems() {
    const items = [];

    companyName &&
      items.push({
        componentProps: {
          children: <span {...getItemWrapperProps()}>{companyName}</span>,
          emphasis: false,
        },
        tagName: "span",
      });

    return items;
  }

  function getActionItems() {
    const items = [];

    isLargeOrXlarge &&
      items.push({
        componentProps: {
          children: (
            <Caption {...getAddAccountCaptionProps()}>
              <span onClick={showCaption}>
                {i18n("AnonymousInformation__ADD_RAPID_REWARDS_ACCOUNT")}
              </span>
            </Caption>
          ),
          size: "fontSize13",
        },
      });

    items.push({
      componentProps: {
        children: i18n("DesktopHeaderControl__LOG_OUT"),
        onClick: handleLogout,
        size: "fontSize13",
      },
    });

    return items;
  }

  function getItemWrapperProps(className) {
    return {
      className: classNames(styles.itemWithSeparator, className),
    };
  }

  function getAddAccountCaptionProps() {
    return {
      adjoiningContent: (
        <div className={styles.loginPopup} ref={captionRef}>
          <LoginForm
            heading={i18n("AnonymousInformation__LOGIN_HEADING")}
            onLoggedIn={handleLoggedIn}
            subheading={i18n("AnonymousInformation__LOGIN_SUBHEADING")}
          />
        </div>
      ),
      alignment: "center",
      bestFit: false,
      location: captionLocation,
      pointerAlignment: "center",
      pointerXOffset: 0,
      role: "navigation",
      showPointer: true,
      stackingContext: isCaptionVisible,
    };
  }

  function handleLoggedIn() {
    hideCaption();
  }
};

AnonymousInformation.displayName = "AnonymousInformation";
AnonymousInformation.propTypes = {
  /** Indicates whether the app is using a dark Header background, to be passed to Link component for styling. */
  light: PropTypes.bool,
};
